import { Toolbar } from "client/Components";
import { ReactElement, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { useFollowAction } from "./useFollowAction";
import { ReactionsButton } from "client/Features/Reactions/ReactionsButton/ReactionsButton";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { HubDataSource } from "client/Api/Hub/hub";
import { ReactionKind } from "client/Api/Hub/common";
import {
	EventStatisticsModel,
	registerReaction,
	unregisterReaction,
	updateReaction
} from "client/Api/Hub/Events/eventViewer";
import { getHubUrlForEvent } from "../../EventsPage";

export type EventActionsProps = {
	statistics: EventStatisticsModel;
	onStatisticsRetrieved: (statistics: EventStatisticsModel) => void;
	eventId: number;
	areCommentsEnabled: boolean;
};

export const EventActions = (props: EventActionsProps): ReactElement | null => {
	const {
		statistics,
		onStatisticsRetrieved,
		eventId,
		areCommentsEnabled
	} = props;

	const sendReaction = (reaction: ReactionKind): Promise<EventStatisticsModel> => {
		if (statistics.currentUserReaction === ReactionKind.NoReaction) {
			return registerReaction(eventId, reaction);
		}

		if (reaction === statistics.currentUserReaction) {
			return unregisterReaction(eventId);
		}

		return updateReaction(eventId, reaction);
	};

	const handleReactionClick = async (reaction: ReactionKind): Promise<void> => {
		try {
			const statistics: EventStatisticsModel = await sendReaction(reaction);
			onStatisticsRetrieved(statistics);
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			throw e;
		}
	};

	const eventLink = useMemo(
		(): string => {
			const result: string = getHubUrlForEvent({
				source: HubDataSource.Event,
				itemId: eventId
			});

			return result;
		},
		[eventId]
	);

	const [
		followIcon,
		followText,
		followClickHandler
	] = useFollowAction(
		eventId,
		statistics.isCurrentUserFollowingComments,
		onStatisticsRetrieved
	);

	return (
		<Toolbar
			moreButtonKind="secondary"
		>
			<Toolbar.Custom
				responsiveBehavior="always-visible"
				renderNormalMode={() => (
					<ReactionsButton
						userReaction={statistics.currentUserReaction}
						onReactionClick={handleReactionClick}
						kind="secondary"
					/>
				)}
				renderCollapsedMode={() => { throw new Error("Does not support collapse"); }}
			/>

			{areCommentsEnabled && (
				<Toolbar.Button
					kind="secondary"
					trackingId="follow"
					text={localize(followText)}
					icon={followIcon}
					onClick={followClickHandler}
				/>
			)}

			<Toolbar.CopyButton
				value={eventLink}
				kind="secondary"
				text={localize("_CopyLink")}
				icon="link"
				trackingId="copy-link"
			/>
		</Toolbar>
	);
};

EventActions.displayName = "EventActions";
